import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PrintReport = ({ games, ticketData, stats, appSettings, onClose, currentEvent, partnerOptions = [], currentUser = null }) => {
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [expenseReport, setExpenseReport] = useState(null);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  // Download the Business Expense Report as a PDF (client-side via html2canvas + jsPDF).
  // Captures the business-expense-report section (incl. Schedule A, signatures,
  // and Schedule B) and paginates across A4 landscape pages.
  const handleDownloadExpensePDF = async () => {
    const node = document.querySelector('[data-testid="business-expense-report"]');
    if (!node) return;
    setPdfDownloading(true);
    try {
      // Give fonts a tick to settle before snapshotting
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const safeEvent = (eventName || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const stamp = new Date().toISOString().slice(0, 10);
      pdf.save(`business-expense-report-${safeEvent}-${stamp}.pdf`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('PDF generation failed', err);
      alert('Could not generate the PDF. Please try the Print Report option instead.');
    } finally {
      setPdfDownloading(false);
    }
  };
  // Get event name and season from settings or current event
  const eventName = currentEvent?.name || appSettings?.app_title?.split(' - ')[0] || 'Event';
  const seasonYear = appSettings?.season_year || currentEvent?.season || '2025-26';
  
  // Calculate cost per ticket dynamically
  const regularGames = games.filter(g => g.season_type !== 'playoff' && g.type !== 'playoff').length;
  const playoffGames = games.filter(g => g.season_type === 'playoff' || g.type === 'playoff').length;
  const totalSeats = appSettings?.total_seats || 6;
  
  const totalRegularContributions = appSettings?.partners?.reduce((sum, p) => sum + (p.amount_paid || 0), 0) || 0;
  const regularCostPerTicket = regularGames > 0 && totalSeats > 0 ? totalRegularContributions / (regularGames * totalSeats) : 0;
  
  const totalPlayoffContributions = appSettings?.playoff_partners?.reduce((sum, p) => sum + (p?.amount_paid || 0), 0) || 0;
  const playoffCostPerTicket = totalPlayoffContributions > 0 && playoffGames > 0 
    ? totalPlayoffContributions / (playoffGames * totalSeats) 
    : 0;

  // Fetch business expense report
  useEffect(() => {
    const fetchExpenseReport = async () => {
      if (!currentEvent?.id && !appSettings?.event_id) return;
      setExpenseLoading(true);
      try {
        const eventId = currentEvent?.id || appSettings?.event_id;
        const userEmail = currentUser?.email || '';
        const resp = await axios.get(`${API}/expense-report/${eventId}`, {
          headers: { 'X-User-Email': userEmail }
        });
        if (resp.data?.success && resp.data.report?.length > 0) {
          setExpenseReport(resp.data);
        }
      } catch {}
      setExpenseLoading(false);
    };
    fetchExpenseReport();
  }, [currentEvent, appSettings, currentUser]);
    
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === undefined || amount === null) {
      return '$0.00';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Dynamic function to get assignments for all partners
  const getGameAssignments = (gameIdx) => {
    const data = ticketData[gameIdx] || ticketData[String(gameIdx)];
    const partnerAssignments = {};
    
    // Initialize all partner assignments
    partnerOptions.forEach(p => {
      partnerAssignments[p.id] = { tickets: 0, parking: 0 };
    });
    
    // Collect guest (non-partner) ticket assignments — anything in
    // `ticket_assignments` is a Friends & Family / business contact; we surface
    // them so the partner knows who got the seats.
    const guestNames = [];
    if (data) {
      // Count tickets and parking for each partner
      (data.tickets || []).forEach(t => {
        if (t && partnerAssignments[t]) {
          partnerAssignments[t].tickets++;
        }
      });
      (data.parking || []).forEach(p => {
        if (p && partnerAssignments[p]) {
          partnerAssignments[p].parking++;
        }
      });
      const ta = data.ticket_assignments || {};
      Object.values(ta).forEach(entry => {
        if (entry && entry.name) guestNames.push(entry.name);
      });
    }

    return {
      partners: partnerAssignments,
      hasScore: data ? (data.played === true || (data.mavs_score > 0 && data.opponent_score > 0)) : false,
      score: data && (data.mavs_score > 0 || data.opponent_score > 0) ? `${data.mavs_score}-${data.opponent_score}` : '-',
      result: data && data.played === true && data.mavs_score === data.opponent_score ? 'T'
        : data && data.mavs_score > data.opponent_score ? 'W'
        : data && data.mavs_score < data.opponent_score ? 'L'
        : '-',
      notes: data?.notes || '',
      guests: guestNames,
    };
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmailReport = async () => {
    setEmailSending(true);
    setEmailStatus(null);
    
    try {
      // Prepare partner stats data for the email
      const partnerStatsData = partnerStats.map(partner => {
        const partnerConfig = appSettings?.partners?.find(p => p.id === partner.id);
        const contribution = partnerConfig?.amount_paid || 0;
        return {
          id: partner.id,
          name: partner.name || partner.id,
          tickets: partner.tickets || 0,
          parking: partner.parking || 0,
          contribution: contribution,
          fairShare: partner.used || 0,
          balance: partner.balance || 0
        };
      });
      
      // Calculate totals
      const totals = {
        totalTickets: partnerStats.reduce((sum, p) => sum + (p.tickets || 0), 0),
        totalParking: partnerStats.reduce((sum, p) => sum + (p.parking || 0), 0),
        totalContribution: partnerStats.reduce((sum, p) => {
          const partnerConfig = appSettings?.partners?.find(pc => pc.id === p.id);
          return sum + (partnerConfig?.amount_paid || 0);
        }, 0)
      };
      
      // Build game-by-game breakdown data
      const gamesData = games
        .map((game, idx) => ({ game, originalIdx: game.game_index !== undefined ? game.game_index : idx }))
        .sort((a, b) => new Date(a.game.date) - new Date(b.game.date))
        .map(({ game, originalIdx }, chronologicalIdx) => {
          const assignments = getGameAssignments(originalIdx);
          const isPlayoffGame = game.season_type === 'playoff' || game.type === 'playoff';
          const costPerTicket = isPlayoffGame ? playoffCostPerTicket : regularCostPerTicket;
          
          // Build partner tickets object
          const partnerTickets = {};
          partnerOptions.forEach(p => {
            partnerTickets[p.id] = assignments.partners[p.id]?.tickets || 0;
          });
          
          return {
            gameNum: chronologicalIdx + 1,
            type: game.type || game.season_type || 'regular',
            date: formatDate(game.date),
            opponent: game.opponent,
            score: assignments.score,
            result: assignments.result,
            costPerTicket: costPerTicket,
            partnerTickets: partnerTickets,
            notes: assignments.notes
          };
        });
      
      // Get partner IDs for column headers
      const partnerIds = partnerOptions.map(p => p.id);
      
      const response = await axios.post(`${API}/superadmin/send-report-email`, {
        event_id: currentEvent?.id || 'mavericks-2025',
        event_name: eventName,
        season_year: seasonYear,
        partner_stats: partnerStatsData,
        totals: totals,
        games: gamesData,
        partner_ids: partnerIds
      });
      
      if (response.data.success) {
        setEmailStatus({
          type: 'success',
          message: `Full report sent to ${response.data.sent_count} partner(s)!`
        });
      } else {
        setEmailStatus({
          type: 'error',
          message: response.data.error || 'Failed to send email'
        });
      }
    } catch (error) {
      setEmailStatus({
        type: 'error',
        message: error.response?.data?.error || error.message || 'Failed to send email'
      });
    } finally {
      setEmailSending(false);
    }
  };

  // Get partner stats from stats object
  const partnerStats = stats.partnerStats || [];
  
  // Color classes for partners
  const partnerColors = [
    { bg: 'bg-blue-50', border: 'border-blue-300', header: 'text-blue-700', headerBg: 'bg-blue-700', cell: 'text-blue-700 bg-blue-50', totalBg: 'bg-blue-900' },
    { bg: 'bg-orange-50', border: 'border-orange-300', header: 'text-orange-700', headerBg: 'bg-orange-600', cell: 'text-orange-700 bg-orange-50', totalBg: 'bg-orange-800' },
    { bg: 'bg-green-50', border: 'border-green-300', header: 'text-green-700', headerBg: 'bg-green-600', cell: 'text-green-700 bg-green-50', totalBg: 'bg-green-800' },
    { bg: 'bg-purple-50', border: 'border-purple-300', header: 'text-purple-700', headerBg: 'bg-purple-600', cell: 'text-purple-700 bg-purple-50', totalBg: 'bg-purple-800' },
  ];

  return createPortal(
    <div className="fixed inset-0 bg-white z-[200] overflow-auto print:static print:overflow-visible print-report-container">
      {/* Print Button - Hide when printing */}
      <div className="no-print sticky top-0 bg-white border-b-2 border-gray-300 p-4 flex justify-between items-center shadow-md z-10">
        <h2 className="text-xl font-bold text-gray-900">{eventName} {seasonYear} Season Ticket Report</h2>
        <div className="flex items-center gap-2">
          {emailStatus && (
            <span className={`px-3 py-1 rounded text-sm ${emailStatus.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {emailStatus.message}
            </span>
          )}
          <button
            onClick={handleEmailReport}
            disabled={emailSending}
            className="px-4 py-2 bg-green-600 text-white rounded font-semibold hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
          >
            {emailSending ? '📧 Sending...' : '📧 Email to Partners'}
          </button>
          {expenseReport && expenseReport.report.length > 0 && (
            <button
              data-testid="download-expense-pdf-btn-top"
              onClick={handleDownloadExpensePDF}
              disabled={pdfDownloading}
              className="px-4 py-2 bg-amber-600 text-white rounded font-semibold hover:bg-amber-700 disabled:bg-amber-400 disabled:cursor-not-allowed"
            >
              {pdfDownloading ? '📄 Generating…' : '📄 Download Expense PDF'}
            </button>
          )}
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-blue-600 text-white rounded font-semibold hover:bg-blue-700"
          >
            🖨️ Print Report
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded font-semibold hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>

      {/* Report Content - Optimized for Printing */}
      <div className="p-8 max-w-7xl mx-auto print:max-w-none">
        {/* Header */}
        <div className="text-center mb-6 pb-4 border-b-2 border-gray-300">
          <h1 className="text-4xl font-bold mb-3 text-gray-900">{eventName} Ticket Usage Report</h1>
          <p className="text-xl font-semibold text-gray-700">{seasonYear} Season • {games.length} Home Games</p>
          <p className="text-sm text-gray-600 mt-2">Generated: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        {/* Summary Statistics — one horizontal row per partner */}
        <div className="mb-6 p-4 bg-gray-100 border-2 border-gray-300 rounded-lg">
          <h2 className="text-xl font-bold mb-3 text-gray-900 border-b-2 border-gray-400 pb-2">Summary</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-gray-600 border-b border-gray-300">
                  <th className="text-left py-2 pr-4 font-semibold">Partner</th>
                  <th className="text-right py-2 px-3 font-semibold">Tickets</th>
                  <th className="text-right py-2 px-3 font-semibold">Parking</th>
                  <th className="text-right py-2 px-3 font-semibold">Contribution</th>
                  <th className="text-right py-2 px-3 font-semibold">Amount Due</th>
                  <th className="text-right py-2 pl-3 font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {partnerStats.map((partner, idx) => {
                  const colors = partnerColors[idx % partnerColors.length];
                  const partnerConfig = appSettings?.partners?.find(p => p.id === partner.id);
                  const contribution = partnerConfig?.amount_paid || 0;
                  const balance = partner.balance || 0;
                  const balanceLabel = balance < 0 ? 'Under' : balance > 0 ? 'Over' : 'Even';
                  const balanceClass =
                    balance < 0 ? 'text-green-700' : balance > 0 ? 'text-red-700' : 'text-gray-700';
                  return (
                    <tr
                      key={partner.id}
                      className={`${colors.bg} border-b ${colors.border} last:border-b-0`}
                    >
                      <td className={`py-3 pr-4 pl-3 font-bold ${colors.header} whitespace-nowrap`}>
                        {partner.name || partner.id}{' '}
                        <span className="font-semibold opacity-80">({partner.id})</span>
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-gray-900">
                        {partner.tickets}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-gray-900">
                        {partner.parking}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-gray-900">
                        {formatCurrency(contribution)}
                      </td>
                      <td className="py-3 px-3 text-right font-semibold tabular-nums text-gray-900">
                        {formatCurrency(partner.used || 0)}
                      </td>
                      <td className={`py-3 pl-3 pr-3 text-right font-bold tabular-nums ${balanceClass} whitespace-nowrap`}>
                        {formatCurrency(Math.abs(balance))}{' '}
                        <span className="text-[10px] font-semibold uppercase">{balanceLabel}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Game-by-Game Breakdown */}
        <div>
          <h2 className="text-2xl font-bold mb-4 text-gray-900 border-b-2 border-gray-400 pb-2">Game-by-Game Breakdown</h2>
          <table className="w-full border-collapse border-2 border-gray-400 text-sm">
            <thead>
              <tr className="bg-gray-800 text-white">
                <th className="border border-gray-400 p-3 text-left font-bold">#</th>
                <th className="border border-gray-400 p-3 text-left font-bold">Date</th>
                <th className="border border-gray-400 p-3 text-left font-bold">Opponent</th>
                <th className="border border-gray-400 p-3 text-center font-bold">Score</th>
                <th className="border border-gray-400 p-3 text-center font-bold">Result</th>
                <th className="border border-gray-400 p-3 text-center font-bold">Cost/Tix</th>
                {partnerOptions.map((partner, idx) => (
                  <th key={partner.id} className={`border border-gray-400 p-3 text-center font-bold ${partnerColors[idx % partnerColors.length].headerBg}`}>
                    {partner.name || partner.id} Tix
                  </th>
                ))}
                <th className="border border-gray-400 p-3 text-left font-bold">Guests</th>
                <th className="border border-gray-400 p-3 text-left font-bold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {games
                .map((game, idx) => ({ game, originalIdx: game.game_index !== undefined ? game.game_index : idx }))
                .sort((a, b) => new Date(a.game.date) - new Date(b.game.date))
                .map(({ game, originalIdx }, chronologicalIdx) => {
                const assignments = getGameAssignments(originalIdx);
                const isPlayoffGame = game.season_type === 'playoff' || game.type === 'playoff';
                const costPerTicket = isPlayoffGame ? playoffCostPerTicket : regularCostPerTicket;
                
                return (
                  <tr key={originalIdx} className={assignments.hasScore ? 'bg-blue-100' : 'bg-white hover:bg-gray-50'}>
                    <td className="border border-gray-400 p-2 font-medium text-gray-900">
                      {chronologicalIdx + 1}
                      {game.type === 'preseason' && <span className="text-xs text-gray-600 font-semibold"> (Pre)</span>}
                      {isPlayoffGame && <span className="text-xs text-red-600 font-semibold"> (PO)</span>}
                    </td>
                    <td className="border border-gray-400 p-2 whitespace-nowrap text-gray-900">{formatDate(game.date)}</td>
                    <td className="border border-gray-400 p-2 text-gray-900 font-medium">{game.opponent}</td>
                    <td className="border border-gray-400 p-2 text-center font-mono font-semibold text-gray-900">{assignments.score}</td>
                    <td className="border border-gray-400 p-2 text-center">
                      {assignments.result === 'W' && <span className="text-green-700 font-bold text-lg">W</span>}
                      {assignments.result === 'L' && <span className="text-red-700 font-bold text-lg">L</span>}
                      {assignments.result === '-' && <span className="text-gray-400">-</span>}
                    </td>
                    <td className="border border-gray-400 p-2 text-center font-semibold text-gray-900">
                      {formatCurrency(costPerTicket)}
                    </td>
                    {partnerOptions.map((partner, idx) => (
                      <td key={partner.id} className={`border border-gray-400 p-2 text-center font-bold text-lg ${partnerColors[idx % partnerColors.length].cell}`}>
                        {assignments.partners[partner.id]?.tickets || '-'}
                      </td>
                    ))}
                    <td className="border border-gray-400 p-2 text-sm text-gray-700">
                      {assignments.guests.length > 0 ? (
                        <span data-testid={`guests-${originalIdx}`} className="font-medium text-indigo-700">
                          {assignments.guests.join(', ')}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="border border-gray-400 p-2 text-sm text-gray-700">
                      {assignments.notes || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {/* Totals Row - After table so it only appears once */}
          <div className="mt-2 flex bg-gray-800 text-white font-bold rounded">
            <div className="flex-1 p-3 text-right text-lg">TOTALS:</div>
            <div className="w-40 p-3 text-center text-lg border-l-2 border-gray-600">-</div>
            {partnerStats.map((partner, idx) => (
              <div key={partner.id} className={`w-32 p-3 text-center text-lg border-l-2 border-gray-600 ${partnerColors[idx % partnerColors.length].totalBg}`}>
                {partner.tickets}
              </div>
            ))}
            <div className="flex-1 p-3 text-center text-lg border-l-2 border-gray-600"></div>
            <div className="flex-1 p-3 text-center text-lg border-l-2 border-gray-600"></div>
          </div>
        </div>

        {/* Final Settlement */}
        <div className="mt-6 p-6 bg-green-50 border-2 border-green-400 rounded-lg">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 border-b-2 border-green-400 pb-2">Final Settlement</h2>
          <div className="space-y-3 text-lg">
            {/* Base Contributions */}
            <div className="p-4 bg-gray-100 rounded border-2 border-gray-400">
              <p className="text-sm text-gray-600 mb-2">Initial Contributions:</p>
              {partnerStats.map((partner, idx) => {
                const partnerConfig = appSettings?.partners?.find(p => p.id === partner.id);
                const contribution = partnerConfig?.amount_paid || 0;
                return (
                  <div key={partner.id} className="flex justify-between items-center mb-1">
                    <span className="font-semibold text-gray-900">{partner.name || partner.id} paid:</span>
                    <strong className="text-xl text-gray-900">{formatCurrency(contribution)}</strong>
                  </div>
                );
              })}
            </div>

            {/* Actual Usage */}
            <div className="p-4 bg-white rounded border-2 border-gray-400">
              <p className="text-sm text-gray-600 mb-2">Actual Ticket Usage:</p>
              {partnerStats.map((partner, idx) => {
                const colors = partnerColors[idx % partnerColors.length];
                return (
                  <div key={partner.id} className={`flex justify-between items-center p-3 ${colors.bg} rounded border ${colors.border} mb-2`}>
                    <span className="font-semibold text-gray-900">{partner.name || partner.id} used:</span>
                    <strong className={`text-xl ${colors.header}`}>{formatCurrency(partner.used || 0)}</strong>
                  </div>
                );
              })}
            </div>

            {/* Settlement Needed */}
            <div className="p-4 bg-yellow-50 rounded border-2 border-yellow-400">
              <p className="text-sm text-gray-600 mb-2">Settlement Required:</p>
              {(() => {
                // Find partners with balances
                const owes = partnerStats.filter(p => (p.balance || 0) > 0);
                const owed = partnerStats.filter(p => (p.balance || 0) < 0);
                
                if (owes.length === 0 && owed.length === 0) {
                  return (
                    <div className="flex justify-between items-center p-3 bg-green-100 rounded border-2 border-green-400">
                      <span className="font-bold text-gray-900">All Settled Up!</span>
                      <strong className="text-2xl text-green-700">$0.00</strong>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2">
                    {owes.map(partner => (
                      <div key={partner.id} className="flex justify-between items-center p-3 bg-red-100 rounded border-2 border-red-400">
                        <span className="font-bold text-gray-900">{partner.name || partner.id} used over contribution:</span>
                        <strong className="text-2xl text-red-700">{formatCurrency(Math.abs(partner.balance || 0))}</strong>
                      </div>
                    ))}
                    {owed.map(partner => (
                      <div key={partner.id} className="flex justify-between items-center p-3 bg-green-100 rounded border-2 border-green-400">
                        <span className="font-bold text-gray-900">{partner.name || partner.id} has remaining balance:</span>
                        <strong className="text-2xl text-green-700">{formatCurrency(Math.abs(partner.balance || 0))}</strong>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Total Season Cost */}
            <div className="border-t-2 border-gray-400 pt-3 mt-3">
              <div className="flex justify-between items-center p-4 bg-gray-800 text-white rounded">
                <span className="font-bold text-xl">Total Season Cost:</span>
                <strong className="text-2xl">{formatCurrency(partnerStats.reduce((sum, p) => sum + (p.used || 0), 0))}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Business Expense Report Section - Formal Report Layout */}
        {expenseReport && expenseReport.report.length > 0 && (
          <div data-testid="business-expense-report" className="mt-8 page-break-before bg-white" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
            {/* In-section PDF download button (hidden in print + PDF snapshot) */}
            <div className="no-print mb-4 flex justify-end" data-html2canvas-ignore="true">
              <button
                data-testid="download-expense-pdf-btn"
                onClick={handleDownloadExpensePDF}
                disabled={pdfDownloading}
                className="px-4 py-2 bg-amber-600 text-white rounded font-semibold hover:bg-amber-700 disabled:bg-amber-400 disabled:cursor-not-allowed shadow"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                {pdfDownloading ? '📄 Generating PDF…' : '📄 Download Expense PDF'}
              </button>
            </div>
            {/* Letterhead */}
            <div className="border-b-4 border-double border-gray-800 pb-4 mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Business Expense Summary</p>
                  <h2 className="text-3xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                    Business Expense Report
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 italic">
                    Season Ticket Allocations to Business Guests
                  </p>
                </div>
                <div className="text-right text-xs text-gray-700 leading-relaxed">
                  <div><span className="uppercase tracking-wider text-gray-500">Report No.</span> <span className="font-mono font-semibold">BER-{(currentEvent?.id || 'EVT').toUpperCase().slice(0,8)}-{new Date().getFullYear()}</span></div>
                  <div><span className="uppercase tracking-wider text-gray-500">Issued</span> <span className="font-semibold">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
                  <div><span className="uppercase tracking-wider text-gray-500">Period</span> <span className="font-semibold">{seasonYear} Season</span></div>
                </div>
              </div>
            </div>

            {/* Total Expense Callout */}
            <div className="border-2 border-gray-800 bg-gray-50 mb-6">
              <div className="flex items-center justify-between px-6 py-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gray-600 mb-1">Total Reportable Business Expense</p>
                  <p className="text-xs text-gray-500 italic">{expenseReport.totals.games} games • {expenseReport.totals.tickets} tickets • {expenseReport.totals.parking} parking</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold text-gray-900 tracking-tight tabular-nums">{formatCurrency(expenseReport.totals.total_cost)}</p>
                  <p className="text-xs text-gray-600 mt-1">at {formatCurrency(expenseReport.totals.cost_per_ticket)} per ticket</p>
                </div>
              </div>
            </div>

            {/* Itemized Expense Schedule */}
            <div className="mb-6">
              <h3 className="text-base font-bold text-gray-900 uppercase tracking-wider mb-2 border-b-2 border-gray-800 pb-1" style={{ fontFamily: "'Georgia', serif" }}>
                Schedule A — Itemized Game Expenses
              </h3>
              <table className="w-full text-xs border-collapse" style={{ fontFamily: "'Georgia', serif" }}>
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">#</th>
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Date</th>
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Event / Opponent</th>
                    <th className="p-2 text-center border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Final Score</th>
                    <th className="p-2 text-center border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Tix</th>
                    <th className="p-2 text-center border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Park</th>
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">From Partner</th>
                    <th className="p-2 text-right border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Unit Cost</th>
                    <th className="p-2 text-right border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Amount</th>
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Business Guest</th>
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Company</th>
                    <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Purpose / Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseReport.report.map((entry, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 border border-gray-400 text-center text-gray-700 font-mono">{idx + 1}</td>
                      <td className="p-2 border border-gray-400 whitespace-nowrap text-gray-900 font-medium">
                        {entry.date ? formatDate(entry.date) : '-'}
                      </td>
                      <td className="p-2 border border-gray-400 font-semibold text-gray-900">{entry.opponent}</td>
                      <td className="p-2 border border-gray-400 text-center text-gray-900 font-mono">
                        {entry.home_score !== '' && entry.away_score !== '' && entry.home_score !== null && entry.away_score !== null
                          ? `${entry.home_score}–${entry.away_score}` 
                          : '—'}
                      </td>
                      <td className="p-2 border border-gray-400 text-center text-gray-900 font-semibold tabular-nums">{entry.tickets}</td>
                      <td className="p-2 border border-gray-400 text-center text-gray-900 font-semibold tabular-nums">{entry.parking || 0}</td>
                      <td className="p-2 border border-gray-400 text-gray-900">
                        {entry.partner_breakdown && Object.keys(entry.partner_breakdown).length > 0
                          ? Object.entries(entry.partner_breakdown).map(([pid, v]) => {
                              const partnerName = appSettings?.partners?.find(p => p.id === pid)?.name || pid;
                              return `${partnerName} (${v.tickets})`;
                            }).join(', ')
                          : '—'}
                      </td>
                      <td className="p-2 border border-gray-400 text-right text-gray-900 tabular-nums">{formatCurrency(entry.cost_per_ticket)}</td>
                      <td className="p-2 border border-gray-400 text-right font-bold text-gray-900 tabular-nums">{formatCurrency(entry.total_cost)}</td>
                      <td className="p-2 border border-gray-400 text-gray-900 font-medium">{entry.contacts?.join(', ')}</td>
                      <td className="p-2 border border-gray-400 text-gray-700">{(entry.companies && entry.companies.length > 0) ? entry.companies.join(', ') : '—'}</td>
                      <td className="p-2 border border-gray-400 text-gray-700 italic">{entry.notes || '—'}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="bg-gray-900 text-white font-bold">
                    <td colSpan="4" className="p-2 border border-gray-700 text-right uppercase tracking-wider text-[10px]">Schedule A Total</td>
                    <td className="p-2 border border-gray-700 text-center tabular-nums">{expenseReport.totals.tickets}</td>
                    <td className="p-2 border border-gray-700 text-center tabular-nums">{expenseReport.totals.parking}</td>
                    <td className="p-2 border border-gray-700"></td>
                    <td className="p-2 border border-gray-700"></td>
                    <td className="p-2 border border-gray-700 text-right text-base tabular-nums">{formatCurrency(expenseReport.totals.total_cost)}</td>
                    <td colSpan="3" className="p-2 border border-gray-700"></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Schedule B moved below — see after signatures */}

            {/* Certification & Signatures */}
            <div className="mt-8 pt-4 border-t-2 border-gray-800">
              <p className="text-sm text-gray-800 mb-6 leading-relaxed">
                I certify that the above Business Guests were provided the above listed tickets.
              </p>
              <div className="grid grid-cols-2 gap-12 mt-8">
                <div>
                  <div className="border-b border-gray-800 h-10"></div>
                  <p className="text-xs uppercase tracking-wider text-gray-600 mt-1">Signature</p>
                  <p className="text-xs text-gray-500 italic">Date: ____________________</p>
                </div>
                <div>
                  <div className="border-b border-gray-800 h-10"></div>
                  <p className="text-xs uppercase tracking-wider text-gray-600 mt-1">Print Name</p>
                </div>
              </div>
            </div>

            {/* Schedule B - Per-Partner Allocation (Internal Partner Use - own page) */}
            {expenseReport.partner_allocation && expenseReport.partner_allocation.length > 0 && (
              <div data-testid="schedule-b-partner-allocation" className="mt-8" style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                <div className="border-b-4 border-double border-gray-800 pb-4 mb-6">
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-1">Internal — Partner Reconciliation Only</p>
                  <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                    Schedule B — Allocation by Ticket Owner
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 italic">
                    Per-partner share of the total business expense, based on which seats they own that were given to business guests.
                  </p>
                </div>

                <table className="w-full text-sm border-collapse" style={{ fontFamily: "'Georgia', serif" }}>
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="p-2 text-left border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Ticket Owner</th>
                      <th className="p-2 text-center border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Tickets Provided</th>
                      <th className="p-2 text-center border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Parking Provided</th>
                      <th className="p-2 text-right border border-gray-700 font-semibold uppercase tracking-wider text-[10px]">Allocated Expense</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseReport.partner_allocation.map((p, idx) => (
                      <tr key={p.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-2 border border-gray-400 font-semibold text-gray-900">{p.name} <span className="text-gray-500 font-normal">({p.id})</span></td>
                        <td className="p-2 border border-gray-400 text-center text-gray-900 tabular-nums">{p.tickets}</td>
                        <td className="p-2 border border-gray-400 text-center text-gray-900 tabular-nums">{p.parking}</td>
                        <td className="p-2 border border-gray-400 text-right font-bold text-gray-900 tabular-nums">{formatCurrency(p.cost)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-900 text-white font-bold">
                      <td className="p-2 border border-gray-700 uppercase tracking-wider text-[10px]">Schedule B Total</td>
                      <td className="p-2 border border-gray-700 text-center tabular-nums">{expenseReport.partner_allocation.reduce((s, p) => s + p.tickets, 0)}</td>
                      <td className="p-2 border border-gray-700 text-center tabular-nums">{expenseReport.partner_allocation.reduce((s, p) => s + p.parking, 0)}</td>
                      <td className="p-2 border border-gray-700 text-right text-base tabular-nums">{formatCurrency(expenseReport.partner_allocation.reduce((s, p) => s + p.cost, 0))}</td>
                    </tr>
                  </tbody>
                </table>

                <p className="mt-6 text-xs text-gray-600 italic">
                  This schedule is intended for internal partner reconciliation. It is not part of the certified business expense report and may be omitted when submitted to outside accounting.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-gray-700 border-t-2 border-gray-300 pt-4">
          <p className="font-semibold">Regular Season Ticket Price: {formatCurrency(regularCostPerTicket)} per ticket</p>
          {playoffGames > 0 && playoffCostPerTicket > 0 && (
            <p className="font-semibold">Playoff Ticket Price: {formatCurrency(playoffCostPerTicket)} per ticket</p>
          )}
          <p className="text-gray-600 mt-1">Report generated from {eventName} Ticket Tracker</p>
        </div>
      </div>

      {/* Print Footer - only visible when printing */}
      <div className="hidden print:block print-footer">
        <span className="print-footer-left">{eventName} {seasonYear} • Ticket Report</span>
        <span className="print-footer-right">Generated {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: auto;
            margin: 0.5in 0.5in 0.75in 0.5in;
          }
          
          * {
            box-sizing: border-box;
          }
          
          /* Hide everything in the document; show only the print-report path */
          body > * {
            display: none !important;
          }
          
          /* Show the portal-mounted report wrapper */
          body > .print-report-container,
          body > div:has(> .print-report-container),
          body > div > .print-report-container {
            display: block !important;
          }
          
          body * {
            visibility: hidden;
          }
          
          .print-report-container,
          .print-report-container * {
            visibility: visible;
          }
          
          /* Position report at top of page - use static so page breaks work */
          .print-report-container {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100%;
            font-size: 11pt;
            overflow: visible !important;
          }
          
          /* Force any fixed/absolute parents back to static so pagination works */
          .print-report-container.fixed {
            position: static !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          html, body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            height: auto;
            overflow: visible;
            background: white !important;
          }
          
          /* Tighten outer padding for print */
          .print-report-container .p-8 {
            padding: 0 !important;
          }
          
          /* Reduce vertical spacing across the report */
          .print-report-container .mb-6 { margin-bottom: 12px !important; }
          .print-report-container .mb-8 { margin-bottom: 16px !important; }
          .print-report-container .mt-6 { margin-top: 12px !important; }
          .print-report-container .mt-8 { margin-top: 16px !important; }
          .print-report-container .p-6 { padding: 10px !important; }
          .print-report-container .p-4 { padding: 8px !important; }
          
          /* Headings tighter */
          .print-report-container h1 { font-size: 20pt !important; margin-bottom: 6px !important; }
          .print-report-container h2 { font-size: 14pt !important; margin-bottom: 8px !important; padding-bottom: 4px !important; }
          .print-report-container h3 { font-size: 12pt !important; }
          
          /* Force Business Expense Report onto a new page */
          [data-testid="business-expense-report"] {
            page-break-before: always !important;
            break-before: page !important;
          }
          
          /* Table handling - allow flow but keep rows intact */
          table {
            page-break-inside: auto;
            width: 100%;
            border-collapse: collapse;
          }
          
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto;
          }
          
          thead {
            display: table-header-group; /* Repeat header on every page */
          }
          
          tbody {
            display: table-row-group;
          }
          
          th, td {
            padding: 4px 6px !important;
          }
          
          /* Keep small grouped panels intact (summary cards, settlement sub-boxes) */
          .bg-blue-50, .bg-orange-50, .bg-yellow-50, .bg-amber-50, .bg-gray-100 {
            page-break-inside: avoid;
          }
          
          /* Force a clean page break before the Business Expense Report */
          [data-testid="business-expense-report"] {
            page-break-before: always !important;
            break-before: page !important;
            margin-top: 0 !important;
          }
          
          /* Schedule B (partner reconciliation) starts on its own page */
          [data-testid="schedule-b-partner-allocation"] {
            page-break-before: always !important;
            break-before: page !important;
            margin-top: 0 !important;
          }
          .print-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 6px 12px;
            border-top: 1px solid #888;
            font-size: 9pt;
            color: #444;
            background: white;
            display: flex !important;
            justify-content: space-between;
            align-items: center;
          }
          
          .print-footer-left { font-weight: 600; }
          .print-footer-right { font-style: italic; }
        }
      `}</style>
    </div>,
    document.body
  );
};
